import { useCallback } from "react"
import { useInstanceWorkspaceContext } from "../context/instance-workspace-context"
import type { GetArrangement } from "./use-workspace-sync"

/**
 * Reads the live Dockview arrangement blob at call time for the current instance: prefer the engine's
 * live `toJSON()` getter, fall back to the seeded arrangement, else null (panels-only). Shared by the
 * "snapshot the current dashboard" actions (save-as-new, duplicate) so they capture the same WYSIWYG
 * placement. Returns a getter that yields null outside the provider (flag-off path).
 *
 * A null from the live getter always means "API not ready yet," never "genuinely empty" — Dockview's
 * `toJSON()` is non-nullable once the api exists — so falling back to the seeded arrangement is
 * correct in both the no-getter and not-ready-yet cases. [APP-3309]
 */
export function useArrangementGetter(): GetArrangement {
  const instanceWorkspace = useInstanceWorkspaceContext()
  return useCallback<GetArrangement>(
    () => instanceWorkspace?.arrangementGetterRef.current?.()
      ?? (instanceWorkspace?.instanceArrangement as Record<string, unknown> | null | undefined)
      ?? null,
    [instanceWorkspace],
  )
}
